import { PublishedStatus, ScoreType, STATUS_MAP } from '@models/Score'
import { isProduct } from '@models/util/product.util'
import { abbreviateNumber } from '@utils/formatters/number.formatter'
import { isEmpty } from '@utils/helpers'
import Storage from '@utils/storage'

import type { WithRequired } from '@custom-types/utils'
import type Product from '@models/Product'
import type Score from '@models/Score'
import type { TitleModelType } from '@models/Title'
import type { SessionUser } from '@stores/globalData/server-side'

export function getScoreType(score: Score): ScoreType {
  if (score.template) return ScoreType.TEMPLATE
  if (score.searchable) return ScoreType.PUBLISHED
  if (score.everyone_role !== 'none') return ScoreType.SHARED
  if (score.active_product_url) return ScoreType.SELLING
  return ScoreType.PRIVATE
}

export function hasUnsavedChanges(score: Score, user: SessionUser | null): boolean {
  return Storage.hasLocalEdits(score.external_id) || Storage.hasLocalDraft(score.external_id, user)
}

export function isTitle(score: Score): boolean {
  return score.is_title_master || score.is_title_copy
}

export function hasTitleOfType(...types: `${TitleModelType}`[]) {
  return (score: Score): boolean => {
    if (!score.title_type || !isTitle(score)) return false

    return types.includes(score.title_type)
  }
}

/** @deprecated */
export function isFromLibrary(score: Score): boolean {
  return !!score.from_library
}

export function getScoreIcon(score: Score | Product, hover?: boolean, selected?: boolean) {
  if (selected) return 'checkbox-check'
  if (hover) return 'checkbox'

  let icon = 'note'
  if (isProduct(score)) {
    icon = 'music-sm'
  } else if (score.is_soundcheck_score) {
    icon = 'soundcheck'
  } else if (hasTitleOfType('cl', 'ee', 'dam')(score) || isFromLibrary(score)) {
    icon = 'music-sm'
  }
  return icon
}

export function isScore(maybeScore: Score | Product): maybeScore is Score {
  return maybeScore.object_type === 'score'
}

// region Social
export function adjustFavesCount(score: WithRequired<Partial<Score>, 'favorites_count'>): string {
  if (isEmpty(score.favorites_count)) return ''
  return abbreviateNumber(score.favorites_count)
}

export function adjustViewsCount(score: WithRequired<Partial<Score>, 'views_count'>): string {
  if (isEmpty(score.views_count)) return ''
  return abbreviateNumber(score.views_count)
}

export function adjustCommentsCount(score: WithRequired<Partial<Score>, 'comments_count'>): string {
  if (isEmpty(score.comments_count)) return ''
  return abbreviateNumber(score.comments_count)
}
// endregion Social

// region Published Scores
export function isPublishedScore(score: Score): boolean {
  return !!score.published_score_status
}

export function isArchived(score: Score): boolean {
  return isPublishedScore(score) && score.published_score_status === PublishedStatus.ARCHIVED
}

export function isPublished(score: Score): boolean {
  return isPublishedScore(score) && score.published_score_status === PublishedStatus.PUBLISHED
}

export function isFinalized(score: Score): boolean {
  return isPublishedScore(score) && score.published_score_status === PublishedStatus.FINALIZED
}

export function formatPublishedStatus(score: Score): string | null {
  if (!score.published_score_status) return null
  return STATUS_MAP[score.published_score_status]
}
// endregion Published Scores
